// @ts-strict-ignore
import type { WorkflowState } from 'flow/store/redux/state';
import type { Action } from 'flow/store/engine/types';

import { CanvasElementLayers } from '../layout/constants';
import {
  workflowNodeType,
} from './types';
import type {
  WorkflowNode, WorkflowNodeType, NodeState,
  FolderNode,
} from './types';
import type { NodeLineage } from '../layout';
import { matchNode } from 'flow/service/search';
import { ActionType } from 'flow/store/engine/constants';
import { FOLDER_NODE_ID } from './constants';
import type { MergeDiffResult } from 'flow/service/changelog/formatters/mergeDropdownFormatter/types';
import { getIsMergeImpacted } from 'flow/service/changelog/formatters/mergeDropdownFormatter/helpers';
import { includesActionOfType } from 'flow/service/actions';


/**
 * Returns the correct nodeType for a given action
 */
function getNodeTypeForActionType(actionType: Action['type']): WorkflowNodeType {
  /**
   * React Flow has built-in nodes, some of which are named `input` and `output`,
   * so we cannot use those values to render our own nodes. This function provides a
   * React-Flow-safe node type using these considerations
   */
  if (actionType === 'imported_matrix_model') return 'matrixModel';
  if (actionType === 'imported_output_attribute') return 'outputAttribute';
  if (actionType === 'input') return 'inputAttribute';
  if (actionType === 'matrix_model') return 'matrixModel';
  // outcome reasons are rendered as tag nodes
  if (actionType === 'outcome_reason') return 'tag';
  if (actionType === 'output') return 'outputAttribute';
  return actionType as WorkflowNodeType;
}

function computeNodeSearchNodeState(args: {
  searchState: WorkflowState['ui']['search'];
  nodePartial: {
    nodeId: WorkflowNode['id'];
    nodeLabel?: WorkflowNode['data']['label'];
  };
  nodeIdsMatchingSearch: WorkflowNode['id'][];
}) {
  const {
    searchState,
    nodePartial: { nodeId, nodeLabel },
    nodeIdsMatchingSearch,
  } = args;

  const searchQueryMatchesNode = matchNode({ nodeLabel, searchQuery: searchState.query });

  const isNodeSearchMatchIndexed = nodeIdsMatchingSearch[searchState.matchIndex] === nodeId;

  const nodeTypeIsSearchable = isNodeTypeSearchable(extractWorkflowNodeTypeFromId(nodeId));

  if (
    nodeTypeIsSearchable && searchQueryMatchesNode && isNodeSearchMatchIndexed
  ) {
    return 'search-focused';
  }

  if (
    nodeTypeIsSearchable && searchQueryMatchesNode && !isNodeSearchMatchIndexed
  ) {
    return 'search-unfocused';
  }

  if (!nodeTypeIsSearchable || !searchQueryMatchesNode) {
    return 'faded';
  }

  return 'normal';
}

function computeMergeDiffNodeState(args: {
  mergeDiff: MergeDiffResult;
  nodeId: WorkflowNode['id'];
  nodeFolder: WorkflowState['ui']['nodeFolders']['inputAttribute'];
}) {
  const {
    mergeDiff,
    nodeId,
    nodeFolder: { isCollapsed },
  } = args;

  // focus folder node if it is collapsed and contains a merge impacted child
  if (nodeId === FOLDER_NODE_ID.inputAttribute) {
    const mergeItems = [
      ...Array.from(mergeDiff.added),
      ...Array.from(mergeDiff.modified),
    ];

    const isInputMergeImpacted = includesActionOfType(
      { type: ActionType.INPUT, actionIds: mergeItems },
    );

    if (isInputMergeImpacted && isCollapsed) {
      return 'normal';
    }

    return 'faded';
  }

  if (getIsMergeImpacted(mergeDiff, nodeId)) {
    return 'normal';
  }

  return 'faded';
}

export function computeNodeState({
  nodePartial,
  uiStatePartial,
  nodeLineage,
  nodeIdsMatchingSearch = [],
  mergeDiff,
}: {
  nodePartial: {
    nodeId: WorkflowNode['id'];
    nodeLabel?: WorkflowNode['data']['label'];
  };
  uiStatePartial: {
    focusedNodeId: WorkflowState['ui']['focusedNodeId'];
    activeNodeEditorTab: WorkflowState['ui']['activeNodeEditorTab'];
    hoveredNodeId: WorkflowState['ui']['hoveredNodeId'];
    highlightedNodeId: WorkflowState['ui']['highlightedNodeId'];
    searchState?: WorkflowState['ui']['search'];
    nodeFolder: WorkflowState['ui']['nodeFolders']['inputAttribute'];
  };
  nodeLineage?: NodeLineage;
  nodeIdsMatchingSearch?: WorkflowNode['id'][];
  mergeDiff?: MergeDiffResult | null;
},
): NodeState {
  const { nodeId } = nodePartial;
  const {
    activeNodeEditorTab,
    focusedNodeId,
    highlightedNodeId,
    hoveredNodeId,
    searchState,
    nodeFolder,
  } = uiStatePartial;

  if (highlightedNodeId === nodeId) return 'highlighted';
  if (focusedNodeId === nodeId && hoveredNodeId !== nodeId) return 'focused';
  if (focusedNodeId !== nodeId && hoveredNodeId === nodeId) return 'hovered';
  if (focusedNodeId === nodeId && hoveredNodeId === nodeId) return 'focused-hovered';

  if (nodeLineage) {
    const {
      directOutgoers,
      directIncomers,
      extendedOutgoers,
      extendedIncomers,
    } = nodeLineage;

    const hasSearchQuery = Boolean(searchState?.query);

    if (hasSearchQuery) {
      return computeNodeSearchNodeState({
        searchState,
        nodePartial,
        nodeIdsMatchingSearch,
      });
    }

    if (mergeDiff) {
      return computeMergeDiffNodeState({ mergeDiff, nodeId, nodeFolder });
    }

    const allFloatedNodeIdsSet = nodeLineage.all;
    if ((allFloatedNodeIdsSet.size > 0 && !allFloatedNodeIdsSet?.has(nodeId))) {
      return 'faded';
    }

    /**
     * When there's a focused node, we only de-emphasize related nodes when the thresholds or
     * dependents panel is open to contrast the normal state of direct incomers or outgoers,
     * respectively. When the options panel is open, all related nodes fall through to the normal
     * state
     */
    if (
      focusedNodeId
      && (
        (
          /**
           * When the thresholds panel is open, everything that is related, but is not a direct
           * parent of the focused node is de-emphasized. In this case, direct parents fall-through
           * to the `normal` state
           */
          activeNodeEditorTab === 'rule'
          && (
            directOutgoers.has(nodeId)
            || extendedOutgoers.has(nodeId)
            /**
             * a node can be a direct & extended incomer at the same time.
             * deemphasize it when it's only the later
             */
            || (extendedIncomers.has(nodeId) && !directIncomers.has(nodeId))
          )
        )
        || (
          /**
           * When the dependents panel is open, everything that is related, but is not a direct
           * child of the focused node is de-emphasized. In this case, direct children fall-through
           * to the `normal` state
           */
          activeNodeEditorTab === 'dependents'
          && (
            directIncomers.has(nodeId)
            || extendedIncomers.has(nodeId)
            /**
             * a node can be a direct & extended outgoer at the same time.
             * deemphasize it when it's only the later
             */
            || (extendedOutgoers.has(nodeId) && !directOutgoers.has(nodeId))
          )
        )
        || (
          !activeNodeEditorTab
          && !directIncomers.has(nodeId)
          && extendedIncomers.has(nodeId)
        )
      )
    ) {
      return 'deemphasized';
    }
  }

  return 'normal';
}

export function getNodeZIndex(): CanvasElementLayers {
  return CanvasElementLayers.NODES;
}

// todo - find a better place for the node-type-specific functions
export function getImportedTailNodeId(ownerNodeId: WorkflowNode['id']): string {
  return `imported-tail-node__${ownerNodeId}`;
}

export function extractWorkflowNodeTypeFromId(nodeId: WorkflowNode['id']): WorkflowNodeType {
  const regex = /__(\w+)__/g;
  const rawIdType = regex.exec(nodeId)?.[1] as ActionType;

  // Nodes have the same id as their action counterpart, so this is safe to do
  return getNodeTypeForActionType(rawIdType);
}

export function filterNodesBySearch(nodes: WorkflowNode[], searchQuery: string) {
  if (!searchQuery) {
    return [];
  }

  return nodes.filter(node => {
    if (!isNodeTypeSearchable(node.type)) {
      return false;
    }

    return matchNode({
      nodeLabel: getNodeLabel(node),
      searchQuery,
    });
  });
}

export function getNodeLabel(node: WorkflowNode) {
  return node?.data?.label ?? '';
}

export function getNodeIds(nodes: WorkflowNode[]) {
  return nodes.map(node => node.id);
}

export function groupNodesById(nodes: WorkflowNode[]) {
  return nodes.reduce((nodesById, node) => {
    nodesById[node.id] = node;
    return nodesById;
  }, {} as { [id: string]: WorkflowNode });
}


export function isNodeTypeSearchable(nodeType: WorkflowNodeType) {
  const nonSearchableNodeTypes: WorkflowNodeType[] = [
    workflowNodeType.skeleton,
    workflowNodeType.preview,
    workflowNodeType.outcome,
    workflowNodeType.workflow,
    workflowNodeType.importedTailNode,
  ];
  return !nonSearchableNodeTypes.includes(nodeType);
}

/**
 * Builds and returns a map of nodetype -> number of node type occurances in graph
 */
export function countNodesByType(nodes: WorkflowNode[]) {
  const startValue = (
    Object.values(workflowNodeType).reduce((acc, currentType) => {
      acc[currentType] = 0;
      return acc;
    }, {} as Record<WorkflowNodeType, number>)
  );

  return nodes.reduce((countedNodes, node) => {
    countedNodes[node.type] += 1;
    return countedNodes;
  }, startValue);
}

export function isNodeFolderStateCollapsed(
  nodeFolderState: WorkflowState['ui']['nodeFolders']['inputAttribute'],
) {
  return nodeFolderState.isCollapsed;
}

export function isFolderNodeCollapsed(folderNode: FolderNode) {
  return folderNode.data.children.length > 0;
}

export function isFolderNodeId(id: string) {
  return id === FOLDER_NODE_ID.inputAttribute;
}

export function isFolderNode(node: WorkflowNode): node is FolderNode {
  return node.type === workflowNodeType.folder;
}

export function getFolderSize(
  nodeFolderState: WorkflowState['ui']['nodeFolders']['inputAttribute'],
  graphNodes: WorkflowNode[],
) {
  if (nodeFolderState.isCollapsed) {
    // if collapsed, a folder's children is nested in its children array
    const folderNode = graphNodes.find(isFolderNode);
    return folderNode ? folderNode.data.children.length : 0;
  }
  // otherwise, they are flattened into the graph
  const countedNodes = countNodesByType(graphNodes);
  return countedNodes[workflowNodeType.inputAttribute];
}

export function filterOutUnsetTagNodes(nodes: WorkflowNode[], unsetTagNodeIds: string[]) {
  return nodes.filter(node => !unsetTagNodeIds.includes(node.id));
}

export function filterNodesByLineage(nodes: WorkflowNode[], lineage: NodeLineage) {
  return nodes.filter(node => lineage.all.has(node.id));
}
